import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { useViewMode } from '../contexts/ViewModeContext';
import { 
  Search, Plus, List, LayoutGrid, Calendar as CalendarIcon, Edit, Trash2, 
  MoreVertical, User, CalendarDays, ChevronLeft, ChevronRight, CheckCircle, 
  Clock, AlertTriangle, RefreshCw, CalendarCheck, CalendarClock, Flame, Zap, X,
  ArrowUp, ArrowDown, ArrowUpDown, Briefcase
} from 'lucide-react';
import TaskModal from './TaskModal';
import TaskFilters from './TaskFilters';
import EmptyState from './EmptyState';
import { getLighterColor } from '../lib/clientColors';
import { formatDate } from '../lib/dateUtils';
import { SearchableSelect } from './ui/searchable-select';

// Compute due-date state + matching color/badge (mirrors AdminTasksPage for consistency)
const getDueStatus = (due_date, status) => {
  if (status === 'Completed') return { urgency: 'completed', colorClass: 'text-neutral-400', pill: null };
  if (!due_date) return { urgency: 'none', colorClass: 'text-neutral-500', pill: null };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const due = new Date(due_date); due.setHours(0, 0, 0, 0);
  if (due < today) return {
    urgency: 'overdue', colorClass: 'text-red-600 font-semibold',
    pill: { text: 'OVERDUE', className: 'text-red-700 bg-red-100 border-red-200' }
  };
  if (due.getTime() === today.getTime()) return {
    urgency: 'today', colorClass: 'text-amber-600 font-semibold',
    pill: { text: 'TODAY', className: 'text-amber-700 bg-amber-100 border-amber-200' }
  };
  if (due.getTime() === tomorrow.getTime()) return {
    urgency: 'tomorrow', colorClass: 'text-yellow-600 font-medium',
    pill: { text: 'TOMORROW', className: 'text-yellow-700 bg-yellow-100 border-yellow-200' }
  };
  return { urgency: 'normal', colorClass: 'text-neutral-500', pill: null };
};

const BACKEND_URL = '';
const employeeApi = axios.create({ baseURL: BACKEND_URL, timeout: 30000 });
employeeApi.interceptors.request.use((config) => { const token = localStorage.getItem('employee_token'); if (token) config.headers.Authorization = `Bearer ${token}`; return config; });

// Task Statistics Component with Clickable Filters
// Now uses server-side stats for accurate counts across ALL data (not just loaded)
function TaskStatistics({ serverStats, isMobile, activeFilter, onFilterClick }) {
  const stats = [
    { key: 'my_pending', label: 'My Pending', value: serverStats?.my_pending || 0, icon: Briefcase, gradient: 'metric-gradient-indigo' },
    { key: 'new', label: 'New Tasks', value: serverStats?.new || 0, icon: Clock, gradient: 'metric-gradient-cyan' },
    { key: 'overdue', label: 'Overdue', value: serverStats?.overdue || 0, icon: AlertTriangle, gradient: 'metric-gradient-red' },
    { key: 'revision', label: 'Revision', value: serverStats?.revision || 0, icon: RefreshCw, gradient: 'metric-gradient-orange' },
    { key: 'today', label: 'Due Today', value: serverStats?.today || 0, icon: CalendarCheck, gradient: 'metric-gradient-emerald' },
    { key: 'tomorrow', label: 'Due Tomorrow', value: serverStats?.tomorrow || 0, icon: CalendarClock, gradient: 'metric-gradient-teal' },
    { key: 'week', label: 'Due This Week', value: serverStats?.week || 0, icon: CalendarIcon, gradient: 'metric-gradient-blue' },
    { key: 'high', label: 'High Priority', value: serverStats?.high || 0, icon: Flame, gradient: 'metric-gradient-rose' },
    { key: 'critical', label: 'Critical', value: serverStats?.critical || 0, icon: Zap, gradient: 'metric-gradient-violet' }
  ];

  if (isMobile) {
    return (
      <div className="grid grid-cols-2 gap-2 mb-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const isActive = activeFilter === stat.key;
          return (
            <button key={stat.key} onClick={() => onFilterClick(isActive ? null : stat.key)}
              className={`${stat.gradient} rounded-2xl p-3 text-left transition-all active:scale-[0.98] ${isActive ? 'ring-2 ring-white/60 ring-offset-2 shadow-lg' : ''}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-white/70 truncate">{stat.label}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
      {stats.map((stat) => {
        const Icon = stat.icon;
        const isActive = activeFilter === stat.key;
        return (
          <button key={stat.key} onClick={() => onFilterClick(isActive ? null : stat.key)}
            className={`${stat.gradient} rounded-2xl p-4 text-left transition-all hover:scale-[1.02] hover:shadow-lg ${isActive ? 'ring-2 ring-white/60 ring-offset-2 shadow-xl scale-[1.02]' : ''}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-white/70">{stat.label}</p>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Mobile Task Card Component with Quick Status Update
function MobileTaskCard({ task, onEdit, onDelete, onStatusChange, statusColors, priorityColors, clients }) {
  const statuses = ['New', 'In Progress', 'Review', 'Revision', 'Completed'];
  const canEdit = task.can_edit !== false; // Default to true if not specified (for admin)
  const canDelete = task.can_delete !== false; // Default to true if not specified (for admin)

  const handleQuickStatusChange = (newStatus) => {
    if (newStatus !== task.status && canEdit) {
      onStatusChange(task._id, newStatus);
    }
  };

  // Get next logical status for quick advance
  const getNextStatus = () => {
    const currentIndex = statuses.indexOf(task.status);
    if (currentIndex < statuses.length - 1) {
      return statuses[currentIndex + 1];
    }
    return null;
  };

  const nextStatus = getNextStatus();

  // Get assignee display name
  const getAssigneeName = () => {
    if (task.assignee_name && task.assignee_name !== 'Unassigned') {
      return task.assignee_name.split(' ')[0];
    }
    if (task.primary_assignee_name) {
      return task.primary_assignee_name.split(' ')[0];
    }
    return 'Unassigned';
  };

  // Check if task is overdue
  const isOverdue = () => {
    if (!task.due_date || task.status === 'Completed') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(task.due_date) < today;
  };

  // Get client color
  const getClientColor = () => {
    if (!task.client_id || !clients) return null;
    const client = clients.find(c => c._id === task.client_id);
    return client?.color || null;
  };

  const clientColor = getClientColor();
  const isCompleted = task.status === 'Completed';
  const dueStatus = getDueStatus(task.due_date, task.status);

  return (
    <div 
      className={`glass-card rounded-xl mb-3 overflow-hidden ${isCompleted ? 'opacity-60' : ''}`}
      style={{ borderLeft: clientColor ? `4px solid ${clientColor}` : undefined }}
    >
      <div className="p-4">
        {/* Top Row: Title + Edit Button */}
        <div className="flex items-start justify-between mb-1">
          <div className="flex-1 pr-2">
            <h3 className={`font-semibold text-[#1a1a2e] line-clamp-2 ${isCompleted ? 'line-through text-neutral-500' : ''}`}>{task.title}</h3>
            {dueStatus.pill && (
              <span className={`inline-block mt-1 px-1.5 py-0.5 rounded border text-[10px] font-bold tracking-wide ${dueStatus.pill.className}`}>
                {dueStatus.pill.text}
              </span>
            )}
          </div>
          {canEdit && (
            <button 
              onClick={(e) => { e.stopPropagation(); onEdit(task); }} 
              className="p-1.5 text-neutral-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-xl flex-shrink-0"
            >
              <Edit className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Client Name with Color Badge */}
        {task.client_name && (
          <div className="flex items-center gap-1.5 mb-2">
            {clientColor && (
              <span 
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: clientColor }}
              />
            )}
            <p className="text-sm text-neutral-600">{task.client_name}</p>
          </div>
        )}

        {/* Status + Priority Row */}
        <div className="flex items-center gap-2 mb-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[task.status]}`}>
            {task.status}
          </span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityColors[task.priority]}`}>
            {task.priority}
          </span>
        </div>

        {/* Due Date + Assignee Row */}
        <div className="flex items-center justify-between text-xs text-neutral-500 mb-3">
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {getAssigneeName()}
          </span>
          {task.due_date && (
            <span className={`flex items-center gap-1 ${dueStatus.colorClass}`}>
              <CalendarDays className="w-3 h-3" />
              {formatDate(task.due_date)}
            </span>
          )}
        </div>

        {/* Minimal Move to Next Status Button - Only if can edit */}
        {nextStatus && canEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); handleQuickStatusChange(nextStatus); }}
            className="w-full py-2 px-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl font-medium text-xs flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all"
          >
            <ChevronRight className="w-3.5 h-3.5" />
            Move to {nextStatus}
          </button>
        )}
      </div>
    </div>
  );
}

// Inline Editable Cell - respects can_edit permission
function InlineEditCell({ value, type, options, onSave, className, canEdit = true }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const handleSave = (override) => { const next = override !== undefined ? override : editValue; if (next !== value) onSave(next); setIsEditing(false); };
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setEditValue(value); setIsEditing(false); } };
  
  if (!canEdit) {
    return <span className={className || ''}>{type === 'date' && value ? formatDate(value) : (value || '-')}</span>;
  }
  
  if (isEditing) {
    if (type === 'select') return (
      <SearchableSelect
        value={editValue}
        onChange={(v) => { setEditValue(v); setTimeout(() => handleSave(v), 0); }}
        triggerClassName="h-8 min-w-[130px] text-sm rounded border-yellow-300 bg-white focus:ring-2 focus:ring-yellow-500"
        options={options.map(opt => ({ value: opt, label: opt }))}
      />
    );
    if (type === 'date') return <input type="date" value={editValue || ''} onChange={(e) => setEditValue(e.target.value)} onBlur={handleSave} onKeyDown={handleKeyDown} autoFocus className="px-2 py-1 border border-yellow-300 rounded text-sm focus:ring-2 focus:ring-yellow-500" />;
    return <input type="text" value={editValue || ''} onChange={(e) => setEditValue(e.target.value)} onBlur={handleSave} onKeyDown={handleKeyDown} autoFocus className="px-2 py-1 border border-yellow-300 rounded text-sm focus:ring-2 focus:ring-yellow-500 w-full" />;
  }
  return <span onClick={() => { setEditValue(value); setIsEditing(true); }} className={`cursor-pointer hover:bg-yellow-50 px-1 py-0.5 rounded ${className || ''}`} title="Click to edit">{type === 'date' && value ? formatDate(value) : (value || '-')}</span>;
}

// Task Row with inline editing - Updated columns: Title, Client, Due Date, Assignee, Status, Priority
function TaskRow({ task, onOpenModal, onInlineUpdate, onDelete, statusColors, priorityColors, employees, clients }) {
  const statuses = ['New', 'In Progress', 'Review', 'Revision', 'Completed'];
  const priorities = ['Low', 'Medium', 'High', 'Critical'];
  const canEdit = task.can_edit !== false; // Default to true if not specified (for admin)
  const canDelete = task.can_delete !== false; // Default to true if not specified (for admin)
  const isCompleted = task.status === 'Completed';
  
  // Check if task is overdue
  const isOverdue = () => {
    if (!task.due_date || task.status === 'Completed') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(task.due_date) < today;
  };

  // Get assignee options for dropdown
  const assigneeOptions = employees?.map(e => ({ id: e._id, name: e.personal?.full_name || e.name || 'Unknown' })) || [];

  // Get client color
  const getClientColor = () => {
    if (!task.client_id || !clients) return null;
    const client = clients.find(c => c._id === task.client_id);
    return client?.color || null;
  };

  const clientColor = getClientColor();
  const dueStatus = getDueStatus(task.due_date, task.status);
  
  return (
    <tr 
      className={`hover:bg-white/60 transition-colors group ${isCompleted ? 'opacity-60' : ''}`}
      style={{ 
        backgroundColor: clientColor ? getLighterColor(clientColor, 0.05) : undefined,
        borderLeft: clientColor ? `3px solid ${clientColor}` : undefined
      }}
    >
      {/* Task Title with urgency pill */}
      <td className="px-4 py-3">
        <InlineEditCell 
          value={task.title} 
          type="text" 
          onSave={(v) => onInlineUpdate(task._id, { title: v })} 
          className={`font-semibold ${isCompleted ? 'line-through text-neutral-500' : 'text-[#1a1a2e]'}`} 
          canEdit={canEdit}
        />
        {dueStatus.pill && (
          <span className={`inline-block mt-1 px-1.5 py-0.5 rounded border text-[10px] font-bold tracking-wide ${dueStatus.pill.className}`}>
            {dueStatus.pill.text}
          </span>
        )}
      </td>
      {/* Client with Color Badge */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {clientColor && (
            <span 
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: clientColor }}
            />
          )}
          <span className={isCompleted ? 'text-neutral-400' : 'text-neutral-600'}>{task.client_name || 'N/A'}</span>
        </div>
      </td>
      {/* Due Date */}
      <td className="px-4 py-3">
        <InlineEditCell 
          value={task.due_date} 
          type="date" 
          onSave={(v) => onInlineUpdate(task._id, { due_date: v })} 
          className={isCompleted ? 'text-neutral-400' : dueStatus.colorClass} 
          canEdit={canEdit}
        />
      </td>
      {/* Assignee */}
      <td className={`px-4 py-3 ${isCompleted ? 'text-neutral-400' : 'text-neutral-600'}`}>{task.assignee_name || 'Unassigned'}</td>
      {/* Status */}
      <td className="px-4 py-3">
        <InlineEditCell 
          value={task.status} 
          type="select" 
          options={statuses} 
          onSave={(v) => onInlineUpdate(task._id, { status: v })} 
          className={`px-2 py-1 rounded text-xs font-medium ${statusColors[task.status]}`} 
          canEdit={canEdit}
        />
      </td>
      {/* Priority */}
      <td className="px-4 py-3">
        <InlineEditCell 
          value={task.priority} 
          type="select" 
          options={priorities} 
          onSave={(v) => onInlineUpdate(task._id, { priority: v })} 
          className={`px-2 py-1 rounded text-xs font-medium ${priorityColors[task.priority]}`} 
          canEdit={canEdit}
        />
      </td>
      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          {canEdit && (
            <button onClick={() => onOpenModal(task)} className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded" title="Edit in Modal">
              <Edit className="w-4 h-4" />
            </button>
          )}
          {canDelete && (
            <button onClick={() => onDelete(task._id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// Draggable Kanban Card - Shows: Title, Client, Due Date, Assignee, Status, Priority
function DraggableKanbanCard({ task, onEdit, onDelete, onDragStart, statusColors, priorityColors, clients }) {
  const [showMenu, setShowMenu] = useState(false);
  const canEdit = task.can_edit !== false; // Default to true if not specified (for admin)
  const canDelete = task.can_delete !== false; // Default to true if not specified (for admin)
  
  // Check if task is overdue
  const isOverdue = () => {
    if (!task.due_date || task.status === 'Completed') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(task.due_date) < today;
  };

  // Get client color
  const getClientColor = () => {
    if (!task.client_id || !clients) return null;
    const client = clients.find(c => c._id === task.client_id);
    return client?.color || null;
  };

  const clientColor = getClientColor();
  const dueStatus = getDueStatus(task.due_date, task.status);

  return (
    <div 
      draggable={canEdit} 
      onDragStart={(e) => canEdit && onDragStart(e, task)} 
      className={`glass-card p-4 hover:shadow-soft ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'} overflow-hidden`}
      style={{ 
        borderLeft: clientColor ? `4px solid ${clientColor}` : undefined,
        backgroundColor: clientColor ? getLighterColor(clientColor, 0.03) : undefined
      }}
    >
      {/* Header with Menu */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="font-semibold text-[#1a1a2e] line-clamp-2">{task.title}</h4>
          {dueStatus.pill && (
            <span className={`inline-block mt-1 px-1.5 py-0.5 rounded border text-[10px] font-bold tracking-wide ${dueStatus.pill.className}`}>
              {dueStatus.pill.text}
            </span>
          )}
        </div>
        {(canEdit || canDelete) && (
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)} className="text-neutral-400 hover:text-neutral-600 p-1">
              <MoreVertical className="w-4 h-4" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-6 bg-white border rounded-xl shadow-lg z-10 py-1 min-w-[100px]">
                {canEdit && (
                  <button onClick={() => { setShowMenu(false); onEdit(); }} className="w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-white/60 flex items-center gap-2">
                    <Edit className="w-3 h-3" /> Edit
                  </button>
                )}
                {canDelete && (
                  <button onClick={() => { setShowMenu(false); onDelete(); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Client with Color Badge */}
      {task.client_name && (
        <div className="mb-2">
          <span 
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium"
            style={{
              backgroundColor: clientColor ? getLighterColor(clientColor, 0.15) : '#F3F4F6',
              color: clientColor || '#374151',
              borderLeft: clientColor ? `3px solid ${clientColor}` : undefined
            }}
          >
            {clientColor && (
              <span 
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: clientColor }}
              />
            )}
            {task.client_name}
          </span>
        </div>
      )}
      
      {/* Priority Badge */}
      <div className="mb-2">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityColors[task.priority]}`}>
          {task.priority}
        </span>
      </div>
      
      {/* Assignee */}
      <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1">
        <User className="w-3 h-3" />
        <span>{task.assignee_name || 'Unassigned'}</span>
      </div>
      
      {/* Due Date */}
      {task.due_date && (
        <div className={`flex items-center gap-2 text-xs ${dueStatus.colorClass}`}>
          <CalendarDays className="w-3 h-3" />
          <span>{formatDate(task.due_date)}</span>
        </div>
      )}
    </div>
  );
}

// Kanban Column with Drop Zone
function KanbanColumn({ status, color, tasks, onEditTask, onDeleteTask, onDragStart, onDrop, isMobile, statusColors, priorityColors, clients }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e) => { e.preventDefault(); setIsDragOver(false); const taskId = e.dataTransfer.getData('taskId'); if (taskId) onDrop(taskId, status); };
  const columnTasks = tasks.filter(t => t.status === status);
  return (
    <div className={`soft-card overflow-hidden ${isMobile ? 'min-w-[260px]' : ''}`}>
      <div className={`px-4 py-3 border-b-4 ${color}`}><h3 className="font-bold text-[#1a1a2e] text-base">{status}</h3><p className="text-sm text-neutral-500">{columnTasks.length} tasks</p></div>
      <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={`p-3 space-y-3 overflow-y-auto transition-colors ${isMobile ? 'min-h-[300px] max-h-[400px]' : 'min-h-[400px] max-h-[calc(100vh-400px)]'} ${isDragOver ? 'bg-yellow-50 border-2 border-dashed border-yellow-300' : 'bg-white/40'}`}>
        {columnTasks.map(task => <DraggableKanbanCard key={task._id} task={task} onEdit={() => onEditTask(task)} onDelete={() => onDeleteTask(task._id)} onDragStart={onDragStart} statusColors={statusColors} priorityColors={priorityColors} clients={clients} />)}
        {columnTasks.length === 0 && <p className="text-neutral-400 text-sm text-center py-8">Drop tasks here</p>}
      </div>
    </div>
  );
}

// Calendar View - Mobile Responsive
function TaskCalendarView({ tasks, onEditTask, onCreateTask, isMobile }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = isMobile ? ["S", "M", "T", "W", "T", "F", "S"] : ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const startingDay = firstDayOfMonth.getDay();
  const totalDays = lastDayOfMonth.getDate();
  const today = new Date(); today.setHours(0,0,0,0);
  const getTasksForDate = (day) => { const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`; return tasks.filter(t => t.due_date === dateStr); };
  const statusColors = { 'New': 'bg-gray-400', 'In Progress': 'bg-blue-500', 'Review': 'bg-purple-500', 'Revision': 'bg-yellow-500', 'Completed': 'bg-green-500' };
  const calendarDays = []; for (let i = 0; i < startingDay; i++) calendarDays.push(null); for (let day = 1; day <= totalDays; day++) calendarDays.push(day);
  const handleDateClick = (day) => { if (!day) return; const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`; onCreateTask(dateStr); };
  return (
    <div className={`glass-card ${isMobile ? 'p-3' : 'p-6'}`}>
      <div className={`flex items-center justify-between ${isMobile ? 'mb-3' : 'mb-6'}`}>
        <h2 className={`font-bold text-[#1a1a2e] ${isMobile ? 'text-base' : 'text-xl'}`}>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
        <div className="flex items-center gap-1">
          <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))} className={`hover:bg-neutral-100 rounded-xl ${isMobile ? 'p-1.5' : 'p-2'}`}><ChevronLeft className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} /></button>
          <button onClick={() => setCurrentDate(new Date())} className={`text-yellow-600 hover:bg-yellow-50 rounded-xl ${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm'}`}>Today</button>
          <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))} className={`hover:bg-neutral-100 rounded-xl ${isMobile ? 'p-1.5' : 'p-2'}`}><ChevronRight className={isMobile ? 'w-4 h-4' : 'w-5 h-5'} /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-neutral-200 rounded-xl overflow-hidden">
        {dayNames.map(d => <div key={d} className={`bg-white/40 text-center font-semibold text-neutral-600 ${isMobile ? 'py-2 text-[10px]' : 'py-3 text-xs'}`}>{d}</div>)}
        {calendarDays.map((day, i) => { 
          const dayTasks = day ? getTasksForDate(day) : []; 
          const isToday = day && currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear() && day === today.getDate(); 
          return (
            <div key={i} onClick={() => handleDateClick(day)} className={`bg-white ${isMobile ? 'min-h-[50px] p-1' : 'min-h-[100px] p-2'} ${day ? 'cursor-pointer hover:bg-yellow-50' : ''} ${isToday ? 'ring-2 ring-inset ring-yellow-500' : ''}`}>
              {day && <>
                <div className={`font-medium ${isMobile ? 'text-xs mb-0.5' : 'text-sm mb-1'} ${isToday ? 'text-yellow-600' : 'text-neutral-700'}`}>{day}</div>
                {isMobile ? (
                  dayTasks.length > 0 && <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 mx-auto"></div>
                ) : (
                  <div className="space-y-1">
                    {dayTasks.slice(0, 3).map(task => <div key={task._id} onClick={(e) => { e.stopPropagation(); onEditTask(task); }} className={`${statusColors[task.status]} text-white text-xs px-1.5 py-0.5 rounded truncate cursor-pointer hover:opacity-80`}>{task.title}</div>)}
                    {dayTasks.length > 3 && <div className="text-xs text-neutral-500 font-medium">+{dayTasks.length - 3} more</div>}
                  </div>
                )}
              </>}
            </div>
          ); 
        })}
      </div>
    </div>
  );
}

export default function EmployeeTasksPage({ initialFilter = null, onClearFilter = () => {} }) {
  const { isMobileView } = useViewMode();
  const [tasks, setTasks] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState('list');
  const [viewMode, setViewMode] = useState('my'); // Default to "My Tasks"
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [presetDueDate, setPresetDueDate] = useState(null);
  const [quickFilter, setQuickFilter] = useState(initialFilter);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const PAGE_SIZE = 50;
  // Server-side statistics - accurate counts from ALL data
  const [serverStats, setServerStats] = useState({
    new: 0, overdue: 0, revision: 0, today: 0, tomorrow: 0, week: 0, high: 0, critical: 0
  });
  // Sorting state - default: due_date ascending (closest first), completed at bottom
  const [sortConfig, setSortConfig] = useState({ key: 'due_date', direction: 'asc' });
  const [filters, setFilters] = useState({
    clients: [],
    departments: [],
    employees: [],
    taskTypes: [],
    subtypes: [],
    statuses: [],
    priorities: [],
    dateFrom: '',
    dateTo: ''
  });
  
  // Ref for infinite scroll
  const loadMoreRef = React.useRef(null);

  // Build query params for server-side filtering
  const buildQueryParams = (page = 1) => {
    const params = { page, page_size: PAGE_SIZE, view_mode: viewMode };
    
    // Quick filter (server-side)
    if (quickFilter) {
      params.quick_filter = quickFilter;
    }
    
    // Search
    if (searchQuery) {
      params.search = searchQuery;
    }
    
    // Multi-select filters
    if (filters.clients.length > 0) params.clients = filters.clients.join(',');
    if (filters.departments.length > 0) params.departments = filters.departments.join(',');
    if (filters.employees.length > 0) params.employees = filters.employees.join(',');
    if (filters.taskTypes.length > 0) params.task_types = filters.taskTypes.join(',');
    if (filters.subtypes.length > 0) params.subtypes = filters.subtypes.join(',');
    if (filters.statuses.length > 0) params.statuses = filters.statuses.join(',');
    if (filters.priorities.length > 0) params.priorities = filters.priorities.join(',');
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    
    return params;
  };

  // Debounce search
  const searchTimeoutRef = React.useRef(null);

  // Update quick filter when initialFilter changes (from dashboard)
  useEffect(() => {
    if (initialFilter) {
      setQuickFilter(initialFilter);
      if (initialFilter === 'my') setViewMode('my');
      else if (initialFilter === 'department') setViewMode('department');
      else if (initialFilter === 'created') setViewMode('created');
      else setViewMode('all');
    }
  }, [initialFilter]);

  // Fetch tasks when filters change
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchTasks(1, false);
    }, searchQuery ? 300 : 0);
    
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [viewMode, quickFilter, searchQuery, filters]);
  
  useEffect(() => {
    // Stagger lookup data loads — critical (tasks) fires first via separate useEffect,
    // lookup data loads with small delays to avoid hammering the server
    const loadLookupData = async () => {
      // These are cached server-side (2 min TTL), so fast after first call
      await fetchTaskTypes();
      await fetchClients();
      fetchDepartments();
      fetchEmployees();
    };
    // Small delay so fetchTasks (from the other useEffect) gets priority
    const t = setTimeout(loadLookupData, 100);
    return () => clearTimeout(t);
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore && currentPage < totalPages) {
          loadMoreTasks();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [currentPage, totalPages, isLoadingMore]);

  const fetchTasks = async (page = 1, append = false, { silent = false } = {}) => { 
    try {
      if (!silent) {
        if (page === 1) {
          setIsLoadingMore(false);
          setIsInitialLoad(true);
        } else {
          setIsLoadingMore(true);
        }
      }
      
      const params = buildQueryParams(page);
      const r = await employeeApi.get('/api/v1/tasks', { params }); 
      const newTasks = r.data?.items || [];
      
      if (append) {
        setTasks(prev => [...prev, ...newTasks]);
      } else {
        setTasks(newTasks);
      }
      
      setTotalTasks(r.data?.total || 0);
      setTotalPages(r.data?.total_pages || 1);
      setCurrentPage(page);
      
      // Update server-side stats (only on first page to avoid redundant updates)
      if (page === 1 && r.data?.stats) {
        setServerStats(r.data.stats);
      }
    } catch (e) { 
      console.error('Error fetching tasks:', e); 
      try {
        const fallback = await employeeApi.get(`/api/employee/tasks?view_mode=${viewMode}`);
        setTasks(fallback.data || []);
        setTotalTasks(fallback.data?.length || 0);
        setTotalPages(1);
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
    } finally {
      setIsLoadingMore(false);
      setIsInitialLoad(false);
    }
  };
  
  const loadMoreTasks = () => {
    if (currentPage < totalPages && !isLoadingMore) {
      fetchTasks(currentPage + 1, true);
    }
  };
  const fetchTaskTypes = async () => { try { const r = await employeeApi.get('/api/task-types'); setTaskTypes(r.data || []); } catch (e) { console.error(e); } };
  const fetchClients = async () => { try { const r = await employeeApi.get('/api/clients'); setClients(r.data || []); } catch (e) { console.error(e); } };
  const fetchDepartments = async () => { try { const r = await employeeApi.get('/api/departments'); setDepartments(r.data || []); } catch (e) { console.error(e); } };
  const fetchEmployees = async () => { try { const r = await employeeApi.get('/api/employee/team'); setEmployees(r.data || []); } catch (e) { console.error(e); } };

  const handleCreateTask = async (d) => {
    try {
      setShowTaskModal(false);
      const res = await employeeApi.post('/api/employee/tasks', d);
      toast.success('Task created');
      if (res.data?._id) {
        setTasks(prev => [res.data, ...prev]);
        setTotalTasks(prev => prev + 1);
      } else {
        fetchTasks(1, false, { silent: true });
      }
      setPresetDueDate(null);
    } catch (e) { toast.error('Failed to create task'); }
  };
  const handleUpdateTask = async (d) => {
    const taskId = editingTask._id;
    setTasks(prev => prev.map(t => t._id === taskId ? { ...t, ...d } : t));
    setShowTaskModal(false);
    setEditingTask(null);
    try {
      await employeeApi.put(`/api/employee/tasks/${taskId}`, d);
      toast.success('Task updated');
    } catch (e) {
      toast.error('Failed to update task');
      fetchTasks(1, false, { silent: true });
    }
  };
  const handleInlineUpdate = async (id, d) => {
    setTasks(prev => prev.map(t => t._id === id ? { ...t, ...d } : t));
    try {
      await employeeApi.put(`/api/employee/tasks/${id}`, d);
      toast.success('Updated');
    } catch (e) {
      toast.error('Failed');
      fetchTasks(1, false, { silent: true });
    }
  };
  const handleStatusChange = async (id, status) => {
    setTasks(prev => prev.map(t => t._id === id ? { ...t, status } : t));
    try {
      await employeeApi.put(`/api/employee/tasks/${id}`, { status });
      toast.success(`Moved to ${status}`);
    } catch (e) {
      toast.error('Failed');
      fetchTasks(1, false, { silent: true });
    }
  };
  const handleDeleteTask = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    const previousTasks = tasks;
    setTasks(prev => prev.filter(t => t._id !== id));
    setTotalTasks(prev => prev - 1);
    try {
      await employeeApi.delete(`/api/employee/tasks/${id}`);
      toast.success('Deleted');
    } catch (e) {
      toast.error('Failed');
      setTasks(previousTasks);
      setTotalTasks(prev => prev + 1);
    }
  };
  const handleCreateTaskWithDate = (date) => { setPresetDueDate(date); setEditingTask(null); setShowTaskModal(true); };
  const handleDragStart = (e, task) => { e.dataTransfer.setData('taskId', task._id); e.dataTransfer.effectAllowed = 'move'; };
  const handleDrop = (taskId, newStatus) => handleStatusChange(taskId, newStatus);

  // Handle quick filter click
  const handleQuickFilterClick = (filterKey) => {
    setQuickFilter(filterKey);
    onClearFilter(); // Clear dashboard filter when user manually changes filter
  };

  const statusColors = { 'New': 'bg-neutral-500 text-white', 'In Progress': 'bg-blue-500 text-white', 'Review': 'bg-purple-500 text-white', 'Revision': 'bg-yellow-500 text-white', 'Completed': 'bg-green-500 text-white' };
  const priorityColors = { 'Low': 'bg-gray-400 text-white', 'Medium': 'bg-blue-400 text-white', 'High': 'bg-orange-500 text-white', 'Critical': 'bg-red-600 text-white' };
  const kanbanColumns = [{ status: 'New', color: 'border-gray-400' }, { status: 'In Progress', color: 'border-blue-500' }, { status: 'Review', color: 'border-purple-500' }, { status: 'Revision', color: 'border-yellow-500' }, { status: 'Completed', color: 'border-green-500' }];

  // Date helpers for statistics display only
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const weekEnd = new Date(today);
  weekEnd.setDate(weekEnd.getDate() + 7);

  // Tasks are already filtered server-side, just use them directly
  const filteredTasks = tasks;

  // Sorting logic with completed tasks always at bottom
  const sortedTasks = useMemo(() => {
    const sorted = [...filteredTasks];
    
    // Priority order for sorting
    const priorityOrder = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
    const statusOrder = { 'Revision': 0, 'In Progress': 1, 'Review': 2, 'New': 3, 'Completed': 4 };
    
    sorted.sort((a, b) => {
      // Always put completed tasks at the bottom
      if (a.status === 'Completed' && b.status !== 'Completed') return 1;
      if (b.status === 'Completed' && a.status !== 'Completed') return -1;
      
      // Sort based on selected column
      let comparison = 0;
      switch (sortConfig.key) {
        case 'title':
          comparison = (a.title || '').localeCompare(b.title || '');
          break;
        case 'client_name':
          comparison = (a.client_name || '').localeCompare(b.client_name || '');
          break;
        case 'due_date':
          // Handle null/undefined dates - push them to the end
          if (!a.due_date && !b.due_date) comparison = 0;
          else if (!a.due_date) comparison = 1;
          else if (!b.due_date) comparison = -1;
          else comparison = a.due_date.localeCompare(b.due_date);
          break;
        case 'assignee_name':
          comparison = (a.assignee_name || '').localeCompare(b.assignee_name || '');
          break;
        case 'status':
          comparison = (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
          break;
        case 'priority':
          comparison = (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
          break;
        default:
          comparison = 0;
      }
      
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [filteredTasks, sortConfig]);

  // Handle column header click for sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-3.5 h-3.5 ml-1 text-neutral-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-yellow-600" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1 text-yellow-600" />;
  };

  // Get filter label for display
  const getFilterLabel = () => {
    const labels = {
      'new': 'New Tasks',
      'overdue': 'Overdue Tasks',
      'revision': 'Revision Tasks',
      'today': 'Due Today',
      'tomorrow': 'Due Tomorrow',
      'week': 'Due This Week',
      'high': 'High Priority',
      'critical': 'Critical Tasks',
      'my': 'My Tasks',
      'created': 'Created by Me',
      'department': 'Department Tasks',
      'all': 'All Tasks'
    };
    return labels[quickFilter] || null;
  };

  return (
    <div className={isMobileView ? 'px-3 py-3 pb-20' : 'p-8'}>
      {/* Header - Mobile */}
      {isMobileView ? (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-lg font-bold text-[#1a1a2e]">Tasks</h1>
              <p className="text-xs text-neutral-500">Manage and track your tasks</p>
            </div>
            <button onClick={() => { setEditingTask(null); setPresetDueDate(null); setShowTaskModal(true); }} 
              className="px-3 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-xl text-sm font-medium flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> New
            </button>
          </div>
          {/* Active Filter Badge - Mobile */}
          {quickFilter && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-neutral-500">Showing:</span>
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                {getFilterLabel()}
                <button onClick={() => { setQuickFilter(null); onClearFilter(); }} className="ml-1 hover:text-yellow-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1a1a2e]">Tasks</h1>
            <p className="text-neutral-600 mt-1">Manage and track your tasks</p>
            {/* Active Filter Badge - Desktop */}
            {quickFilter && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-neutral-500">Filtered by:</span>
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                  {getFilterLabel()}
                  <button onClick={() => { setQuickFilter(null); onClearFilter(); }} className="ml-1 hover:text-yellow-900">
                    <X className="w-4 h-4" />
                  </button>
                </span>
              </div>
            )}
          </div>
          <button onClick={() => { setEditingTask(null); setPresetDueDate(null); setShowTaskModal(true); }} className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-xl hover:opacity-90 flex items-center gap-2"><Plus className="w-4 h-4" /> New Task</button>
        </div>
      )}

      <TaskStatistics serverStats={serverStats} isMobile={isMobileView} activeFilter={quickFilter} onFilterClick={handleQuickFilterClick} />

      {/* View Mode Tabs - Mobile: Scrollable */}
      <div className={`flex items-center border-b border-white/40 ${isMobileView ? 'mb-3 overflow-x-auto' : 'gap-2 mb-6'}`}>
        {[{ key: 'my', label: isMobileView ? 'My' : 'My Tasks' }, { key: 'created', label: isMobileView ? 'Created' : 'Created by Me' }, { key: 'department', label: isMobileView ? 'Dept' : 'Department Tasks' }, { key: 'all', label: isMobileView ? 'All' : 'All Tasks' }].map(tab => (
          <button key={tab.key} onClick={() => setViewMode(tab.key)} className={`font-medium border-b-2 transition-colors whitespace-nowrap ${isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'} ${viewMode === tab.key ? 'border-yellow-600 text-yellow-600' : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'}`} data-testid={`task-tab-${tab.key}`}>{tab.label}</button>
        ))}
      </div>

      {/* Search, Filters, and View Controls */}
      {isMobileView ? (
        <div className="space-y-3 mb-4">
          {/* Search and Filter Row */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input type="text" placeholder="Search tasks..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} 
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500" />
            </div>
            {/* Mobile Filter Button */}
            <TaskFilters 
              filters={filters} 
              onFiltersChange={setFilters} 
              clients={clients} 
              departments={departments} 
              employees={employees} 
              taskTypes={taskTypes} 
              showClientFilter={true} 
              accentColor="yellow"
              isMobile={true}
            />
          </div>
          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-neutral-100 rounded-xl p-1">
            {[
              { key: 'list', icon: List, label: 'List' },
              { key: 'kanban', icon: LayoutGrid, label: 'Board' },
              { key: 'calendar', icon: CalendarIcon, label: 'Calendar' }
            ].map(v => (
              <button key={v.key} onClick={() => setActiveView(v.key)} 
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-xs font-medium transition-colors ${
                  activeView === v.key ? 'bg-white text-yellow-600 shadow-soft' : 'text-neutral-600 hover:text-[#1a1a2e]'
                }`}>
                <v.icon className="w-3.5 h-3.5" />
                <span>{v.label}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-neutral-500">Showing {sortedTasks.length} of {tasks.length} tasks</p>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-1">
              <div className="relative mb-3"><Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" /><input type="text" placeholder="Search tasks..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500" /></div>
              <TaskFilters filters={filters} onFiltersChange={setFilters} clients={clients} departments={departments} employees={employees} taskTypes={taskTypes} showClientFilter={true} accentColor="yellow" />
            </div>
            <div className="flex items-center gap-1 glass-card rounded-xl p-1">
              {['list', 'kanban', 'calendar'].map(v => <button key={v} onClick={() => setActiveView(v)} className={`px-4 py-2 rounded-xl flex items-center gap-2 ${activeView === v ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-soft' : 'text-neutral-700 hover:bg-white/60'}`}>{v === 'list' && <List className="w-4 h-4" />}{v === 'kanban' && <LayoutGrid className="w-4 h-4" />}{v === 'calendar' && <CalendarIcon className="w-4 h-4" />}{v.charAt(0).toUpperCase() + v.slice(1)}</button>)}
            </div>
          </div>
          <div className="text-sm text-neutral-500 mb-4 flex items-center gap-2">
            <span>Showing {sortedTasks.length} of {totalTasks} tasks</span>
            {isInitialLoad && <span className="text-yellow-500 animate-pulse">Loading...</span>}
          </div>
        </>
      )}

      {/* List View - Cards on Mobile, Table on Desktop */}
      {activeView === 'list' && (
        isMobileView ? (
          <div className="space-y-3">
            {isInitialLoad ? (
              <div className="text-center text-neutral-500 py-12 glass-card">
                <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading tasks...
              </div>
            ) : sortedTasks.length === 0 ? (
              <div className="glass-card">
                <EmptyState
                  icon={CheckCircle}
                  title="No tasks found"
                  description="Try adjusting your filters."
                  tone="default"
                  testId="empty-employee-tasks-mobile"
                />
              </div>
            ) : (
              <>
                {sortedTasks.map(task => (
                  <MobileTaskCard 
                    key={task._id} 
                    task={task} 
                    onEdit={(t) => { setEditingTask(t); setShowTaskModal(true); }}
                    onDelete={handleDeleteTask}
                    onStatusChange={handleStatusChange}
                    statusColors={statusColors}
                    priorityColors={priorityColors}
                    clients={clients}
                  />
                ))}
                {/* Infinite scroll trigger */}
                <div ref={loadMoreRef} className="py-4 text-center">
                  {isLoadingMore && (
                    <div className="flex items-center justify-center gap-2 text-neutral-500">
                      <div className="animate-spin w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
                      Loading more...
                    </div>
                  )}
                  {currentPage >= totalPages && tasks.length > 0 && (
                    <span className="text-xs text-neutral-400">All tasks loaded</span>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="soft-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/40 border-b border-white/40">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('title')}
                      className="flex items-center text-sm font-semibold text-neutral-700 hover:text-yellow-600 transition-colors"
                    >
                      Task Title <SortIndicator columnKey="title" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('client_name')}
                      className="flex items-center text-sm font-semibold text-neutral-700 hover:text-yellow-600 transition-colors"
                    >
                      Client <SortIndicator columnKey="client_name" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('due_date')}
                      className="flex items-center text-sm font-semibold text-neutral-700 hover:text-yellow-600 transition-colors"
                    >
                      Due Date <SortIndicator columnKey="due_date" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('assignee_name')}
                      className="flex items-center text-sm font-semibold text-neutral-700 hover:text-yellow-600 transition-colors"
                    >
                      Assignee <SortIndicator columnKey="assignee_name" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('status')}
                      className="flex items-center text-sm font-semibold text-neutral-700 hover:text-yellow-600 transition-colors"
                    >
                      Status <SortIndicator columnKey="status" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button 
                      onClick={() => handleSort('priority')}
                      className="flex items-center text-sm font-semibold text-neutral-700 hover:text-yellow-600 transition-colors"
                    >
                      Priority <SortIndicator columnKey="priority" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/30">
                {sortedTasks.length === 0 ? (
                  <tr><td colSpan="7" className="px-6 py-2">
                    <EmptyState
                      icon={CheckCircle}
                      title="No tasks found"
                      description="Try adjusting your filters."
                      tone="default"
                      testId="empty-employee-tasks-table"
                    />
                  </td></tr>
                ) : (
                  sortedTasks.map(task => (
                    <TaskRow 
                      key={task._id} 
                      task={task} 
                      onOpenModal={(t) => { setEditingTask(t); setShowTaskModal(true); }} 
                      onInlineUpdate={handleInlineUpdate} 
                      onDelete={handleDeleteTask} 
                      statusColors={statusColors} 
                      priorityColors={priorityColors}
                      employees={employees}
                      clients={clients}
                    />
                  ))
                )}
              </tbody>
            </table>
            {/* Infinite scroll trigger for table */}
            <div ref={loadMoreRef} className="py-4 text-center border-t border-white/30">
              {isLoadingMore && (
                <div className="flex items-center justify-center gap-2 text-neutral-500">
                  <div className="animate-spin w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
                  Loading more tasks...
                </div>
              )}
              {currentPage >= totalPages && tasks.length > 0 && !isLoadingMore && (
                <span className="text-xs text-neutral-400">All {totalTasks} tasks loaded</span>
              )}
            </div>
          </div>
        )
      )}

      {/* Kanban View - Horizontal scroll on mobile */}
      {activeView === 'kanban' && (
        <div className={isMobileView ? 'overflow-x-auto pb-4 -mx-3 px-3' : ''}>
          <div className={isMobileView ? 'flex gap-3 min-w-max' : 'grid grid-cols-5 gap-4'}>
            {kanbanColumns.map(col => (
              <KanbanColumn 
                key={col.status} 
                status={col.status} 
                color={col.color} 
                tasks={sortedTasks} 
                onEditTask={(t) => { setEditingTask(t); setShowTaskModal(true); }} 
                onDeleteTask={handleDeleteTask} 
                onDragStart={handleDragStart} 
                onDrop={handleDrop} 
                isMobile={isMobileView}
                statusColors={statusColors}
                priorityColors={priorityColors}
                clients={clients}
              />
            ))}
          </div>
        </div>
      )}

      {activeView === 'calendar' && <TaskCalendarView tasks={sortedTasks} onEditTask={(t) => { setEditingTask(t); setShowTaskModal(true); }} onCreateTask={handleCreateTaskWithDate} isMobile={isMobileView} />}

      <TaskModal isOpen={showTaskModal} onClose={() => { setShowTaskModal(false); setEditingTask(null); setPresetDueDate(null); }} onSubmit={editingTask ? handleUpdateTask : handleCreateTask} onDelete={handleDeleteTask} api={employeeApi} userType="employee" existingTask={editingTask} presetDueDate={presetDueDate} />
    </div>
  );
}
